import DescriptionIcon from "@mui/icons-material/Description";
import PhotoCameraBackIcon from "@mui/icons-material/PhotoCameraBack";
import {
  Box,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DEFAULT_S3_REGION, resolveStoredMediaFileUrl } from "@repo/utils";
import { t } from "i18next";
import { useMemo, useState } from "react";
import type { Accept } from "react-dropzone";
import { useDropzone } from "react-dropzone";
import type { FieldError, FieldPath, FieldValues, PathValue } from "react-hook-form";
import { useController, useFormContext } from "react-hook-form";

import useUploadFile from "@/hooks/use-upload-file";
import { downloadFile } from "@/utils/getData";

import type { Document, UploadItemProps, UploadMetadata } from "./types";

import { UploadActions } from "./UploadActions";
import { UploadListItem } from "./UploadContent";
import { UploadPreview } from "./UploadPreview";
import { UploadProgress } from "./UploadProgress";

const EMPTY_DOCUMENT: Document = { key: "", url: "", type: "" };

/** Narrow unknown form field values into the upload row shape without per-call-site assertions. */
function toUploadDocument(value: unknown): Document {
  if (value === null || value === undefined || typeof value !== "object") {
    return EMPTY_DOCUMENT;
  }
  const v = value as Record<string, unknown>;
  return {
    label: typeof v.label === "string" ? v.label : undefined,
    name: typeof v.name === "string" ? v.name : undefined,
    key: typeof v.key === "string" ? v.key : "",
    url: typeof v.url === "string" ? v.url : "",
    type: typeof v.type === "string" ? v.type : undefined,
    metadata:
      v.metadata !== null && v.metadata !== undefined && typeof v.metadata === "object"
        ? (v.metadata as Record<string, unknown>)
        : undefined,
  };
}

function DocumentListItemText({
  documentTitle,
  isPDF,
  isImage,
  updatedAt,
  error,
}: {
  documentTitle: string;
  isPDF: boolean;
  isImage: boolean;
  updatedAt: string;
  error?: FieldError;
}) {
  return (
    <ListItemText
      primary={documentTitle}
      primaryTypographyProps={{
        sx: {
          fontWeight: 600,
          color: "text.primary",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "70%",
          textTransform: "capitalize",
        },
      }}
      secondary={
        <>
          <Typography color="text.secondary" component="span" variant="body2">
            Type: {isPDF ? "PDF" : isImage ? "Image" : "Unknown"}
          </Typography>
          <br />
          <Typography color="text.secondary" component="span" variant="caption">
            {t("common.actions.update-label")} {updatedAt}
          </Typography>
          {error?.message !== undefined && error.message.length > 0 && (
            <Typography color="error" component="span" variant="caption">
              {error.message}
            </Typography>
          )}
        </>
      }
    />
  );
}

export function UploadItem({
  value,
  meta,
  canEdit = true,
  error,
  handleDelete,
  handleFileUploaded,
}: UploadItemProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [openPreview, setOpenPreview] = useState(false);
  const resolvedUrl = useMemo(() => {
    const storedUrl = value?.url?.trim();
    if (storedUrl && /^https?:\/\//i.test(storedUrl)) return storedUrl;
    const bucket = import.meta.env.VITE_S3_BUCKET as string | undefined;
    if (!bucket) return storedUrl;
    return (
      resolveStoredMediaFileUrl(value, {
        bucket,
        region: (import.meta.env.VITE_AWS_REGION as string | undefined) || DEFAULT_S3_REGION,
      }) ?? storedUrl
    );
  }, [value]);
  const isImage =
    value?.type?.includes("image") ||
    Boolean(
      resolvedUrl && /\.jpg$|\.jpeg$|\.png$|\.gif$|\.webp$|\.heic$|\.heif$/i.test(resolvedUrl)
    );
  const isPDF = value?.type?.includes("pdf") || Boolean(resolvedUrl && /\.pdf$/i.test(resolvedUrl));
  const { loading, uploadProgress, onDrop } = useUploadFile({
    maxFileSize: 10,
    maxFiles: 1,
    uploadedFiles: [],
    metadata: { ...meta },
    errorMessages: {
      maxSize: "Le fichier est trop volumineux (max 10MB).",
      fileType: "Type de fichier non supporté.",
      maxFiles: "Un seul fichier est autorisé.",
    },
    fileName: value?.label,
    onFilesUploaded: (newFiles) => {
      if (newFiles.length > 0) {
        const uploadedFile = newFiles[0];
        const mergedMetadata = {
          ...(value?.metadata ?? {}),
          ...(uploadedFile.metadata ?? {}),
        };
        const newDocument: Document = {
          label: value?.label ?? uploadedFile.name,
          name: uploadedFile.name,
          key: uploadedFile.key,
          url: uploadedFile.url,
          type: uploadedFile.originalFile.type,
          metadata: Object.keys(mergedMetadata).length > 0 ? mergedMetadata : undefined,
        };

        void Promise.resolve().then(() => handleFileUploaded?.(newDocument));
      }
    },
  });

  const documentTitle = value?.label ?? value?.name ?? "Untitled Document";
  const hasDocument = Boolean(resolvedUrl);
  const updatedAt = new Date().toLocaleString();
  const isDragDropEnabled = canEdit && !loading;
  const acceptedFileTypes: Accept = isPDF
    ? { "application/pdf": [".pdf"] }
    : { "image/*": [".jpg", ".jpeg", ".png", ".heic", ".heif"] };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: (...args) => {
      void onDrop(...args);
    },
    noClick: true,
    noKeyboard: true,
    multiple: false,
    disabled: !isDragDropEnabled,
    accept: acceptedFileTypes,
  });
  return (
    <>
      <UploadListItem
        {...getRootProps()}
        hasDocument={hasDocument}
        isDragActive={isDragActive}
        isDragDropEnabled={isDragDropEnabled}
        loading={loading}
      >
        <input {...getInputProps()} style={{ display: "none" }} />
        {/* Document Avatar */}
        <ListItemIcon>
          {isImage ? <PhotoCameraBackIcon /> : isPDF ? <DescriptionIcon /> : null}
        </ListItemIcon>

        {/* Document Info */}
        <DocumentListItemText
          documentTitle={documentTitle}
          error={error}
          isImage={isImage}
          isPDF={isPDF}
          updatedAt={updatedAt}
        />

        {/* Upload Progress */}
        {loading && (
          <Box
            sx={{
              width: isMobile ? "100%" : 200,
            }}
          >
            <UploadProgress loading={loading} value={uploadProgress} />
          </Box>
        )}

        {/* Actions */}
        <UploadActions
          canChange={canEdit}
          canDelete={Boolean(resolvedUrl)}
          canOpen={Boolean(resolvedUrl)}
          id={value.key}
          loading={loading}
          onChange={open}
          onDelete={() => handleDelete?.(value.key)}
          onDownload={() => {
            if (resolvedUrl) downloadFile(resolvedUrl, documentTitle);
          }}
          onOpen={() => {
            setOpenPreview(true);
          }}
          readyOnly={!canEdit}
        />
      </UploadListItem>

      {/* Preview Modal */}
      {Boolean(resolvedUrl) && !loading && (
        <UploadPreview
          document={{ ...value, url: resolvedUrl ?? value.url }}
          isImage={isImage}
          onClose={() => setOpenPreview(false)}
          open={openPreview}
        />
      )}
    </>
  );
}

export type UploadFormItemProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  name: TName;
  meta: UploadMetadata;
  canEdit?: boolean;
  handleDelete: (key: string) => void;
  handleEdit?: (file: PathValue<TFieldValues, TName>) => void;
};

export function UploadFormItem<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  meta,
  canEdit = true,
  handleDelete,
  handleEdit,
}: UploadFormItemProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();
  const {
    field,
    fieldState: { error },
  } = useController<TFieldValues, TName>({
    control,
    name,
    rules: { required: "This field is required" },
  });

  const value = toUploadDocument(field.value);

  const handleFileUploaded = (file: Document) => {
    const next = file as PathValue<TFieldValues, TName>;
    field.onChange(next);
    handleEdit?.(next);
  };

  return (
    <UploadItem
      canEdit={canEdit}
      error={error}
      handleDelete={handleDelete}
      handleFileUploaded={handleFileUploaded}
      meta={meta}
      value={value}
    />
  );
}
